import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi } from '../api/company';
import { accountsApi } from '../api/accounts';
import { journalApi } from '../api/journal';
import { invoicesApi } from '../api/invoices';
import { api } from '../api/client';
import { closeReadinessApi, type CloseReadinessDto } from '../api/aiAuthority';
import { getWorkspaceId } from '../api/workspaces';
import { formatMoney } from '../utils/formatMoney';
import { formatDate, today } from '../utils/formatDate';
import LoadingSpinner from '../components/LoadingSpinner';
import MoneyCell from '../components/MoneyCell';
import { useReportScreen } from '../components/ScreenContext';

interface ScannerExecutionResult {
  scannerName: string;
  timestamp: string;
  findings: {
    severity: string;
    title: string;
    description: string;
    confidence: number;
    affectedEntityIds: string[];
    suggestedAction: string;
  }[];
  summary: string;
  totalFindings: number;
  criticalCount: number;
  warningCount: number;
  infoCount: number;
}

interface DecisionLogResponse {
  items: {
    id: string;
    timestamp: string;
    agentName: string;
    authorityLevel: string;
    inputs: string;
    outcome: string;
    confidenceScore: number;
  }[];
  totalCount: number;
}

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasWorkspace = !!getWorkspaceId();

  // Account class labels via i18n
  const ACCOUNT_CLASS_LABELS: Record<string, string> = {
    '1': t('dashboard.longTermAssets'),
    '2': t('dashboard.shortTermAssets'),
    '3': t('dashboard.equity'),
    '4': t('dashboard.liabilities'),
    '5': t('dashboard.sales'),
    '6': t('dashboard.costs'),
  };

  const { data: company, isLoading: companyLoading } = useQuery({
    queryKey: ['company'],
    queryFn: companyApi.get,
    enabled: hasWorkspace,
  });

  const { data: accounts } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsApi.list({ limit: 500 }),
    enabled: hasWorkspace,
  });

  const { data: trialBalance } = useQuery({
    queryKey: ['trial-balance', `${new Date().getFullYear()}-01-01`, today()],
    queryFn: () => accountsApi.trialBalance({
      from: `${new Date().getFullYear()}-01-01`,
      to: today(),
      nonZeroOnly: true,
    }),
    enabled: hasWorkspace,
  });

  const { data: recentEntries } = useQuery({
    queryKey: ['journal-recent'],
    queryFn: () => journalApi.list({ limit: 10 }),
    enabled: hasWorkspace,
  });

  const { data: recentInvoices } = useQuery({
    queryKey: ['invoices-recent'],
    queryFn: () => invoicesApi.list({ direction: 'Made', limit: 5 }),
    enabled: hasWorkspace,
  });

  const { data: recentReceived } = useQuery({
    queryKey: ['invoices-received-recent'],
    queryFn: () => invoicesApi.list({ direction: 'Received', limit: 5 }),
    enabled: hasWorkspace,
  });

  // Scanner results for anomaly/risk summary (POST endpoint)
  const { data: scannerResults } = useQuery<ScannerExecutionResult[]>({
    queryKey: ['scanner-results-dashboard'],
    queryFn: () => api.post('/scanners/execute-all'),
    staleTime: 120_000,
    retry: false,
    enabled: hasWorkspace,
  });

  // Decision log for AI activity tile
  const { data: decisionLog } = useQuery<DecisionLogResponse>({
    queryKey: ['decision-log-dashboard'],
    queryFn: () => api.get('/decision-log', { take: 5 }),
    staleTime: 60_000,
    retry: false,
    enabled: hasWorkspace,
  });

  // Close readiness from dedicated endpoint
  const { data: closeReadiness } = useQuery<CloseReadinessDto>({
    queryKey: ['close-readiness'],
    queryFn: closeReadinessApi.get,
    staleTime: 120_000,
    retry: false,
    enabled: hasWorkspace,
  });

  // Compute close readiness from scanner results — must be above any early return (Rules of Hooks)
  const scannerSummary = useMemo(() => {
    const results = scannerResults ?? [];
    const criticalCount = results.reduce((sum, r) => sum + r.criticalCount, 0);
    const warningCount = results.reduce((sum, r) => sum + r.warningCount, 0);
    const infoCount = results.reduce((sum, r) => sum + r.infoCount, 0);
    const totalFindings = criticalCount + warningCount + infoCount;
    // Readiness score: 100% if no findings, drops with each finding
    const readiness = totalFindings === 0 ? 100 : Math.max(0, 100 - criticalCount * 20 - warningCount * 5 - infoCount);
    return { criticalCount, warningCount, infoCount, totalFindings, readiness };
  }, [scannerResults]);

  // Group trial balance by account class — computed here for screen reporting + rendering
  const byClass: Record<string, { debit: number; credit: number; balance: number }> = {};
  for (const item of trialBalance ?? []) {
    const classKey = String(item.accountNumber).charAt(0);
    if (!byClass[classKey]) byClass[classKey] = { debit: 0, credit: 0, balance: 0 };
    byClass[classKey].debit += item.debitTotal;
    byClass[classKey].credit += item.creditTotal;
    byClass[classKey].balance += item.balance;
  }

  // AI screen context — must be above any early return (Rules of Hooks)
  const entryCount = recentEntries?.length ?? 0;
  const invoiceCount = (recentInvoices?.length ?? 0) + (recentReceived?.length ?? 0);
  useReportScreen(
    `Dashboard: ${company?.name ?? 'A5Suite'} apibendrinimas. ` +
    `Saskaitu klases: ${Object.entries(byClass).map(([cls, d]) => `Klase ${cls} (${ACCOUNT_CLASS_LABELS[cls] ?? ''}): ${formatMoney(d.balance)}`).join(', ')}. ` +
    `Paskutiniai zurnalo irasai: ${entryCount}, saskaitos fakturos: ${invoiceCount}. ` +
    `Close readiness: ${closeReadiness?.overallScore ?? scannerSummary.readiness}%. ` +
    `Anomalijos: ${scannerSummary.totalFindings} (${scannerSummary.criticalCount} critical, ${scannerSummary.warningCount} warnings). ` +
    `Cash position: ${formatMoney(byClass['2']?.balance ?? 0)}, AR: ${formatMoney(byClass['2']?.debit ?? 0)}, AP: ${formatMoney(byClass['4']?.credit ?? 0)}.`,
    {
      recordCount: entryCount + invoiceCount,
      filters: {},
      preview: [
        ...Object.entries(byClass).slice(0, 6).map(([cls, d]) => `Class ${cls}: ${formatMoney(d.balance)}`),
        `Recent entries: ${entryCount}`,
        `Recent invoices: ${invoiceCount}`,
        `Close readiness: ${closeReadiness?.overallScore ?? scannerSummary.readiness}%`,
      ],
    },
  );

  if (companyLoading) return <LoadingSpinner />;

  // Show a setup prompt when there's no active workspace
  if (!hasWorkspace) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
        <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
        </svg>
        <h2 className="text-xl font-semibold text-gray-700">{t('layout.noWorkspace')}</h2>
        <p className="text-gray-500 max-w-md">
          {t('layout.createOrImport')}
        </p>
        <button
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          onClick={() => navigate('/workspaces')}
        >
          {t('layout.manageWorkspaces')}
        </button>
      </div>
    );
  }

  // Cash position: sum of class 2 (short-term assets) balances
  const cashPosition = byClass['2']?.balance ?? 0;

  // AR/AP from class 2 and 4
  const receivables = byClass['2']?.debit ?? 0;
  const payables = byClass['4']?.credit ?? 0;

  // Recent AI activity count
  const aiActivityCount = decisionLog?.totalCount ?? 0;
  const recentAiActions = decisionLog?.items ?? [];

  return (
    <div className="space-y-6">
      {/* Company header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {company?.name ?? 'A5Suite'}
          </h1>
          {company && (
            <p className="text-sm text-gray-500">
              {t('common.code')}: {company.code} | {t('dashboard.vat')}: {company.vatCode} | {company.baseCurrency}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            onClick={() => navigate('/invoices/new')}
          >
            {t('dashboard.invoice')}
          </button>
          <button
            className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100"
            onClick={() => navigate('/journal')}
          >
            {t('dashboard.journalBtn')}
          </button>
        </div>
      </div>

      {/* Close Readiness + Cash Position + Anomalies + AI Activity row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Close Readiness Tile — circle progress */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('dashboard.closeReadiness')}</h3>
            <button
              className="text-xs text-primary-600 hover:underline"
              onClick={() => navigate('/periods/close')}
            >
              {t('common.details')}
            </button>
          </div>
          <div className="flex items-center gap-4">
            {/* Circle progress indicator */}
            <div className="relative w-16 h-16 flex-shrink-0">
              <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r="14" fill="none" stroke="#f3f4f6" strokeWidth="3" />
                <circle
                  cx="18" cy="18" r="14" fill="none"
                  stroke={(() => {
                    const score = closeReadiness?.overallScore ?? scannerSummary.readiness;
                    return score >= 80 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444';
                  })()}
                  strokeWidth="3"
                  strokeDasharray={`${(closeReadiness?.overallScore ?? scannerSummary.readiness) * 0.88} 88`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-sm font-bold ${
                  (closeReadiness?.overallScore ?? scannerSummary.readiness) >= 80 ? 'text-green-600'
                    : (closeReadiness?.overallScore ?? scannerSummary.readiness) >= 50 ? 'text-amber-600'
                    : 'text-red-600'
                }`}>
                  {closeReadiness?.overallScore ?? scannerSummary.readiness}%
                </span>
              </div>
            </div>
            <div className="text-xs text-gray-500 space-y-1">
              {closeReadiness ? (
                <>
                  {closeReadiness.criticalCount > 0 && (
                    <div className="text-red-600 font-medium">{closeReadiness.criticalCount} {t('dashboard.critical')}</div>
                  )}
                  {closeReadiness.warningCount > 0 && (
                    <div className="text-amber-600 font-medium">{closeReadiness.warningCount} {t('dashboard.warnings')}</div>
                  )}
                  {closeReadiness.blockers.map((b, i) => (
                    <div key={i} className="text-gray-500">{b}</div>
                  ))}
                  {closeReadiness.criticalCount === 0 && closeReadiness.warningCount === 0 && (
                    <div className="text-green-600 font-medium">{t('dashboard.allClear')}</div>
                  )}
                </>
              ) : (
                <>
                  {scannerSummary.criticalCount > 0 && (
                    <span className="text-red-600 font-medium">{scannerSummary.criticalCount} {t('dashboard.critical')}</span>
                  )}
                  {scannerSummary.warningCount > 0 && (
                    <span className="text-amber-600 font-medium">{scannerSummary.warningCount} {t('dashboard.warnings')}</span>
                  )}
                  {scannerSummary.totalFindings === 0 && <span className="text-green-600 font-medium">{t('dashboard.allClear')}</span>}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Cash Position Tile */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('dashboard.cashPosition')}</h3>
            <button
              className="text-xs text-primary-600 hover:underline"
              onClick={() => navigate('/banking')}
            >
              {t('dashboard.banking')}
            </button>
          </div>
          <div className="text-3xl font-bold text-gray-900 num">
            <MoneyCell value={cashPosition} />
          </div>
          <div className="mt-2 text-xs text-gray-500">
            {t('dashboard.ar')}: <span className="num font-medium text-blue-600">{formatMoney(receivables)}</span>
            {' | '}
            {t('dashboard.ap')}: <span className="num font-medium text-amber-600">{formatMoney(payables)}</span>
          </div>
        </div>

        {/* Anomaly & Risk Tile */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('dashboard.anomaliesRisk')}</h3>
            <button
              className="text-xs text-primary-600 hover:underline"
              onClick={() => navigate('/ai-audit')}
            >
              {t('common.viewAll')}
            </button>
          </div>
          {scannerSummary.totalFindings === 0 ? (
            <div className="flex items-center gap-2 mt-2">
              <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-green-700 font-medium">{t('dashboard.noAnomalies')}</span>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2 mt-1">
              <div className="text-center">
                <div className="text-lg font-bold text-red-600">{scannerSummary.criticalCount}</div>
                <div className="text-[10px] text-gray-400">{t('dashboard.critical')}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-amber-600">{scannerSummary.warningCount}</div>
                <div className="text-[10px] text-gray-400">{t('dashboard.warnings')}</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-bold text-blue-600">{scannerSummary.infoCount}</div>
                <div className="text-[10px] text-gray-400">{t('dashboard.info')}</div>
              </div>
            </div>
          )}
        </div>

        {/* AI Activity Tile */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('dashboard.aiActivity')}</h3>
            <button
              className="text-xs text-primary-600 hover:underline"
              onClick={() => navigate('/ai-audit')}
            >
              {t('dashboard.decisionLog')}
            </button>
          </div>
          <div className="text-3xl font-bold text-gray-900">{aiActivityCount}</div>
          <div className="text-xs text-gray-400">{t('dashboard.totalDecisions')}</div>
          {recentAiActions.length > 0 && (
            <div className="mt-2 space-y-1">
              {recentAiActions.slice(0, 3).map((action) => (
                <div key={action.id} className="text-xs text-gray-500 truncate">
                  <span className="font-medium text-gray-700">{action.agentName}</span>
                  {' '}
                  <span className="text-gray-400">{formatDate(action.timestamp)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Balance overview by class */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {(['1', '2', '3', '4', '5', '6'] as const).map((cls) => {
          const data = byClass[cls];
          return (
            <div key={cls} className="bg-white rounded-lg border border-gray-200 p-3">
              <div className="text-xs text-gray-500 mb-1">{t('dashboard.class')} {cls}</div>
              <div className="text-xs text-gray-600 mb-1 truncate">{ACCOUNT_CLASS_LABELS[cls]}</div>
              <div className="text-lg font-semibold num">
                <MoneyCell value={data?.balance ?? 0} />
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent journal entries */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <h2 className="font-semibold text-gray-900">{t('dashboard.recentJournal')}</h2>
            <button
              className="text-xs text-primary-600 hover:underline"
              onClick={() => navigate('/journal')}
            >
              {t('common.viewAll')}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="dense w-full">
              <thead>
                <tr>
                  <th className="text-left">{t('common.date')}</th>
                  <th className="text-left">{t('common.docNumber')}</th>
                  <th className="text-left">{t('common.content')}</th>
                  <th className="text-right">{t('dashboard.debit')}</th>
                  <th className="text-right">{t('dashboard.credit')}</th>
                </tr>
              </thead>
              <tbody>
                {recentEntries?.map((entry) => (
                  <tr
                    key={entry.id}
                    className="cursor-pointer hover:bg-blue-50"
                    onClick={() => navigate(`/journal/${entry.id}`)}
                  >
                    <td>{formatDate(entry.date)}</td>
                    <td>{entry.documentNumber}</td>
                    <td className="max-w-32 truncate">{entry.content}</td>
                    <td className="num">{formatMoney(entry.totalDebit)}</td>
                    <td className="num">{formatMoney(entry.totalCredit)}</td>
                  </tr>
                ))}
                {(!recentEntries || recentEntries.length === 0) && (
                  <tr><td colSpan={5} className="text-center text-gray-400 py-4">{t('dashboard.noEntries')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Recent invoices */}
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
            <h2 className="font-semibold text-gray-900">{t('dashboard.recentInvoices')}</h2>
            <button
              className="text-xs text-primary-600 hover:underline"
              onClick={() => navigate('/invoices/made')}
            >
              {t('common.viewAll')}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="dense w-full">
              <thead>
                <tr>
                  <th className="text-left">{t('common.date')}</th>
                  <th className="text-left">{t('common.docNumber')}</th>
                  <th className="text-left">{t('common.person')}</th>
                  <th className="text-right">{t('common.amount')}</th>
                  <th className="text-left">{t('common.type').substring(0,3)}.</th>
                </tr>
              </thead>
              <tbody>
                {[...(recentInvoices ?? []), ...(recentReceived ?? [])]
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .slice(0, 10)
                  .map((inv) => (
                    <tr
                      key={`${inv.direction}-${inv.id}`}
                      className="cursor-pointer hover:bg-blue-50"
                      onClick={() => navigate(`/invoices/${inv.id}?direction=${inv.direction}`)}
                    >
                      <td>{formatDate(inv.date)}</td>
                      <td>{inv.documentNumber}</td>
                      <td className="max-w-32 truncate">{inv.personName ?? `${t('common.person')} ${inv.personId}`}</td>
                      <td className="num">{formatMoney(inv.totalSum)}</td>
                      <td>{inv.direction === 'Made' ? t('dashboard.out') : t('dashboard.in')}</td>
                    </tr>
                  ))}
                {(recentInvoices?.length ?? 0) + (recentReceived?.length ?? 0) === 0 && (
                  <tr><td colSpan={5} className="text-center text-gray-400 py-4">{t('dashboard.noInvoices')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <button
          className="bg-white border border-gray-200 rounded-lg p-4 text-left hover:border-primary-300 hover:bg-primary-50 transition-colors"
          onClick={() => navigate('/accounts/trial-balance')}
        >
          <div className="text-sm font-semibold text-gray-900">{t('dashboard.trialBalance')}</div>
          <div className="text-xs text-gray-500">{t('dashboard.asOfToday')}</div>
        </button>
        <button
          className="bg-white border border-gray-200 rounded-lg p-4 text-left hover:border-primary-300 hover:bg-primary-50 transition-colors"
          onClick={() => navigate('/reports/vat')}
        >
          <div className="text-sm font-semibold text-gray-900">{t('dashboard.vatSummary')}</div>
          <div className="text-xs text-gray-500">{t('dashboard.taxReports')}</div>
        </button>
        <button
          className="bg-white border border-gray-200 rounded-lg p-4 text-left hover:border-primary-300 hover:bg-primary-50 transition-colors"
          onClick={() => navigate('/reports/saft')}
        >
          <div className="text-sm font-semibold text-gray-900">{t('dashboard.saftExport')}</div>
          <div className="text-xs text-gray-500">{t('dashboard.xmlForVmi')}</div>
        </button>
        <button
          className="bg-white border border-gray-200 rounded-lg p-4 text-left hover:border-primary-300 hover:bg-primary-50 transition-colors"
          onClick={() => navigate('/settings')}
        >
          <div className="text-sm font-semibold text-gray-900">{t('dashboard.settingsBtn')}</div>
          <div className="text-xs text-gray-500">{t('dashboard.companyAndAccounts')}</div>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-xs text-gray-500">{t('dashboard.accounts')}</div>
          <div className="text-2xl font-bold text-gray-900">{accounts?.length ?? 0}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-xs text-gray-500">{t('dashboard.trialBalanceItems')}</div>
          <div className="text-2xl font-bold text-gray-900">{trialBalance?.length ?? 0}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-3 text-center">
          <div className="text-xs text-gray-500">{t('dashboard.invoicesMade')}</div>
          <div className="text-2xl font-bold text-gray-900">{recentInvoices?.length ?? 0}</div>
        </div>
      </div>
    </div>
  );
}
